"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Flame, Zap, PhoneCall, TriangleAlert as AlertTriangle, FileText, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, Activity, Droplets, Clock, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const rheemProductLines = [{
  category: "tankless",
  title: "Rheem Tankless Systems",
  series: "IKONIC & Performance Platinum Series",
  description: "High-efficiency condensing and non-condensing gas/electric tankless units offering endless hot water on demand.",
  features: ["Up to 0.96 UEF efficiency rating", "Built-in Wi-Fi and EcoNet technology", "Continuous flow rates up to 11 GPM", "Compact indoor & outdoor configurations"],
  commonFixes: "Flow sensor cleaning, heat exchanger descaling, venting calibration"
}, {
  category: "hybrid",
  title: "Rheem ProTerra Hybrid Electric",
  series: "ProTerra Heat Pump Series",
  description: "Advanced heat pump technology that reduces water heating energy costs up to 75% compared to standard electric tanks.",
  features: ["Up to 4.07 UEF ultra-high efficiency", "Integrated LeakGuard automatic water shut-off", "Demand response & smart grid ready", "Multiple operational heating modes"],
  commonFixes: "Compressor diagnostics, air filter replacement, evaporator coil servicing"
}, {
  category: "traditional",
  title: "Rheem Gladiator & Classic Tanks",
  series: "Gladiator, Performance Plus & Classic",
  description: "Industry-trusted atmospheric gas, power vent, and dual-element electric tank water heaters from 30 to 80 gallons.",
  features: ["Built-in 360-degree leak detection", "Self-cleaning EverKleen dip tubes", "Resistor-grade heating elements", "Low-NOx & ultra-low NOx gas burners"],
  commonFixes: "Thermopile/gas valve replacement, element swaps, anode rod renewals"
}];
const errorCodes = [{
  code: "Error A001 / Code 11",
  issue: "Ignition Failure / No Flame Detected",
  probableCause: "Gas valve closed, dirty flame sensor, or inadequate gas inlet pressure.",
  action: "Inspect gas supply line. If open, request technician service for electronic igniter or burner inspection."
}, {
  code: "Error A012 / Code 12",
  issue: "Flame Loss During Active Operation",
  probableCause: "Unstable gas pressure, high wind backdraft in venting, or carbon buildup on sensors.",
  action: "Clear external vent termination and schedule a professional combustion analysis."
}, {
  code: "Error A029 / Code 29",
  issue: "Condensate Drain Line Blockage",
  probableCause: "Blocked neutralizer or kinked condensate drain tube on condensing tankless units.",
  action: "Ensure drain line is clear of debris. Avoid chemical drain cleaners inside water heater lines."
}, {
  code: "EcoNet Alert / Beeping",
  issue: "LeakGuard Automatic Shutoff Triggered",
  probableCause: "Internal sensor moisture detection or puddle near the tank base.",
  action: "Immediate shutoff activated. Inspect drain pan and contact our emergency repair team immediately."
}];
const troubleshootingFaqs = [{
  q: "Why is my Rheem gas water heater status light blinking?",
  a: "The status light on the Honeywell or Rheem gas control valve flashes diagnostic codes. A normal standby operation is typically one flash every 3 seconds. Multi-flash sequences indicate specific issues like high tank temperature, thermopile voltage drop, or burner ignition lockouts."
}, {
  q: "How often should a Rheem tankless water heater be descaled in Georgia?",
  a: "We recommend descaling your Rheem tankless unit every 12 to 18 months. Mineral sediment build-up in the secondary heat exchanger restricts water flow and lowers efficiency."
}, {
  q: "How does Rheem factory warranty claim support work?",
  a: "Rheem water heaters include manufacturer warranties ranging from 6 to 12 years on tanks and parts depending on model. We verify your unit's serial number, coordinate original OEM replacement parts with Rheem distributors, and perform certified warranty labor."
}, {
  q: "What should I do if my Rheem Gladiator shuts off automatically?",
  a: "Gladiator models feature built-in LeakGuard shutoff valves. If the unit detects water in its sensor rope, it locks the incoming water supply to prevent flooding. Do not force the valve open before checking for leaks around plumbing fittings."
}];
export default function RheemServices() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("all");
  const filteredProducts = activeTab === "all" ? rheemProductLines : rheemProductLines.filter(p => p.category === activeTab);
  return <section id="rheemwaterheaterservices" data-nav="light" className="relative w-full bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-3 border-primary/30 px-3 py-1 text-primary">
            Rheem Water Heater Specialists
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Complete Rheem Water Heater Service, Repair & Replacement
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Certified technical service for all Rheem residential and commercial water heating systems in Jonesboro, GA. Fast dispatch, OEM parts, and warranty diagnostics.
          </p>
        </div>

        {/* Emergency Alert Bar */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-8">
          <Alert className="border-accent/40 bg-accent/10 text-foreground">
            <AlertTriangle className="size-5 text-accent" />
            <AlertTitle className="text-base font-semibold">
              Rheem Water Heater Leaking or Throwing Error Codes?
            </AlertTitle>
            <AlertDescription className="text-sm">
              Our 24/7 emergency plumbers carry authentic Rheem components, igniters, gas control valves, and sensors for same-day recovery.
              <span className="mt-2 block font-medium">
                Call our 24/7 hotline directly:{" "}
                <a href="tel:4708479076" className="font-bold text-primary underline hover:text-primary/80">
                  (470) 847-9076
                </a>
              </span>
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Product Lines Covered */}
        <div className="mt-16">
          <div className="flex flex-col items-start justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-end">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Rheem Product Lines We Service
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Expert repairs and factory-spec installations across all Rheem models.
              </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList className="bg-muted">
                <TabsTrigger value="all">All Models</TabsTrigger>
                <TabsTrigger value="tankless">Tankless</TabsTrigger>
                <TabsTrigger value="hybrid">Hybrid</TabsTrigger>
                <TabsTrigger value="traditional">Traditional</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Cards Grid */}
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((product, index) => <motion.div key={product.title} data-index={index} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex">
                <Card className="flex w-full flex-col justify-between border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardHeader>
                    <div className="mb-2 flex items-center justify-between">
                      <Badge variant="secondary" className="font-semibold text-secondary-foreground">
                        {product.series}
                      </Badge>
                      {product.category === "tankless" && <Flame className="size-5 text-primary" />}
                      {product.category === "hybrid" && <Zap className="size-5 text-accent" />}
                      {product.category === "traditional" && <Droplets className="size-5 text-primary" />}
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {product.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {product.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Key Engineering Specs
                      </p>
                      <ul className="mt-2 space-y-2 text-sm text-foreground">
                        {product.features.map((feat, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 shrink-0 text-primary" />
                            <span>{feat}</span>
                          </li>)}
                      </ul>
                    </div>

                    <Separator />

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Common Repairs Handled
                      </p>
                      <p className="mt-1 text-xs text-foreground/90 font-medium">
                        {product.commonFixes}
                      </p>
                    </div>
                  </CardContent>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="outline" className="w-full justify-between">
                      <Link to="/water-heater-repair/">
                        <span>Schedule Service</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Diagnostic Error Code Guide */}
        <div className="mt-20 rounded-2xl border border-border bg-card p-6 md:p-10 shadow-sm">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-2 text-primary border-primary/30">
              Diagnostic Guide
            </Badge>
            <h3 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Common Rheem Error Codes & Troubleshooting
            </h3>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              Rheem digital displays and EcoNet enabled heaters display specific codes when an operational safety fault is detected. Use this quick reference guide to identify the issue:
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {errorCodes.map((item, i) => <div key={item.code} data-index={i} className="rounded-xl border border-border bg-background p-5 shadow-2xs">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <span className="font-mono text-sm font-bold text-primary">
                    {item.code}
                  </span>
                  <Activity className="size-4 text-muted-foreground" />
                </div>
                <h4 className="mt-3 text-base font-semibold text-foreground">
                  {item.issue}
                </h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Cause: </span>
                  {item.probableCause}
                </p>
                <div className="mt-3 rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Recommended Action: </span>
                  {item.action}
                </div>
              </div>)}
          </div>
        </div>

        {/* Warranty & Guarantee Grid */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Card className="border-border bg-card">
            <CardHeader>
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="size-6" />
              </div>
              <CardTitle className="mt-4 text-xl font-bold">
                Rheem Factory Warranty Support
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                We directly process Rheem manufacturer parts warranty claims. We help verify unit age, model serial registration, and expedite factory-approved replacement tanks and parts.
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardHeader>
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Wrench className="size-6" />
              </div>
              <CardTitle className="mt-4 text-xl font-bold">
                OEM Genuine Parts In Stock
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Our service vehicles carry authentic Rheem thermopiles, gas control valves, electric elements, thermostats, EcoNet modules, and venting components for immediate fixes.
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardHeader>
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Clock className="size-6" />
              </div>
              <CardTitle className="mt-4 text-xl font-bold">
                24/7 Local Jonesboro Coverage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Fast emergency dispatch throughout Jonesboro and surrounding Clayton County communities. We ensure your hot water is restored safely and up to current Georgia plumbing codes.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* FAQs Accordion */}
        <div className="mt-20">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="outline" className="mb-2 text-primary border-primary/30">
              Got Questions?
            </Badge>
            <h3 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Rheem Water Heater FAQs
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Clear answers to the most common Rheem troubleshooting and repair questions.
            </p>
          </div>

          <div className="mx-auto mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="w-full">
              {troubleshootingFaqs.map((faq, index) => <AccordionItem key={index} value={`faq-${index}`} data-index={index}>
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-20 rounded-2xl bg-primary px-6 py-10 text-primary-foreground sm:px-12 sm:py-14">
          <div className="flex flex-col items-center justify-between gap-8 md:flex-row">
            <div className="max-w-2xl text-center md:text-left">
              <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Need Fast Rheem Water Heater Repair in Jonesboro?
              </h3>
              <p className="mt-2 text-sm text-primary-foreground/90 sm:text-base">
                Speak with our local technicians now. We diagnose error codes, fix gas and electric faults, and provide upfront pricing before any work begins.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Button asChild size="lg" className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90 shadow-md">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>(470) 847-9076</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/contact/">
                  <span>Request Service</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}